import React, { useState, useCallback, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Stack,
    Box,
    CircularProgress,
    Alert,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { apiHandler } from '../../../../utils/api/responseHandler';
import { quoteValidator } from '../../../../utils/api/validation';
import type { QuoteData } from '../../../../types/quoter/types';
import config from '../../../../config';
import LPAdjuster from './LPAdjuster';
import { useComponentVersion } from '../../../../hooks/quoter/useComponentVersion';
import logger from '../../../../utils/logging/logger';
import { useApiService } from '../../../../utils/api/enhancedApiService';

const componentName = 'CoreSailInfo';

interface CoreSailInfoProps {
    quoteId: number;
    sailId: number;
    showLp: boolean;
}

// Expanded local state
interface CoreSailInfoState {
    quoteData: QuoteData | null;
    isLoading: boolean;
    error: string | null;
}

const CoreSailInfo: React.FC<CoreSailInfoProps> = ({
    quoteId,
    sailId,
    showLp,
}) => {
    const [state, setState] = useState<CoreSailInfoState>({
        quoteData: null,
        isLoading: false,
        error: null
    });

    const apiService = useApiService();
    //const { setUserId } = useAuth(); Was setQuoteGroupId

    const [quoteData, setQuoteData] = useState<QuoteData | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    //++ Use the component version hook
    const { masterVersion } = useComponentVersion('coreSailInfo', {
        onVersionChange: async (newVersion) => {
            logger.log('LogComponentUpdates', componentName, `Updating masterVersion from ${masterVersion} to `, newVersion);
            await fetchQuoteData();
        }
    });

    // Fetch quote data from API
    const fetchQuoteData = useCallback(async () => {
        if (!quoteId) return;

        const controller = new AbortController();

        try {
            logger.log('LogComponentUpdates', componentName, `fetchQuoteData: initialized`);

            setState(prev => ({
                ...prev,
                isLoading: true,
                error: null
            }));

            const response = await apiService.fetch<QuoteData>(
                `${config.API_URL}/api/quotes/${quoteId}`,
                {
                    validateFn: (data) => quoteValidator.validate(data),
                    signal: controller.signal
                }
            );

            if (response.error) throw response.error;

            if (response.data) {
                setQuoteData(response.data);

                //// Check if we have a QuoteId and persist it
                //if (response.data.QuoteId) {
                //    // Import the setQuoteGroupId function
                //    setQuoteGroupId(response.data.QuoteGroupId);
                //    logger.log('LogQuoteCreation', componentName, 'Persisted QuoteGroupId:', response.data.QuoteGroupId);
                //}

                setState(prev => ({
                    ...prev,
                    quoteData: response.data || null,
                    isLoading: false,
                    error: null
                }));
            }
            else {
                logger.log('LogIssue', componentName, `!!!!!fetchQuoteData() Failed to load core sail info data!!!!!`);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch quote data';
            console.error('CoreSailInfo: Error fetching data:', errorMessage);

            setState(prev => ({
                ...prev,
                initialized: true,
                isLoading: false,
                error: errorMessage
            }));
        }
    }, [quoteId, setState]);

    // Initial data load on mount - ensures data loads even if version system doesn't trigger
    useEffect(() => {
        fetchQuoteData();
    }, [fetchQuoteData]);

    const handleLPError = useCallback((error: string) => {
        setState(prev => ({
            ...prev,
            isLoading: false,
            error: `LP Update Error: ${error}`
        }));
    }, []);

    if (state.isLoading || !quoteData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={1}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (state.error) {
        return (
            <Alert severity="error" sx={{ py: 0.5 }}>
                {state.error}
            </Alert>
        );
    }

    return (
        <Card sx={{
            border: 0,
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: 'none',
            //background: THEME_ACTIVE.TOGGLE_BACKGROUND,
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
        }}>
            <CardContent sx={{
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                p: 1,
                m: 0,
            }}>

                {/* Main vertical layout - Area first, then LP */}
                <Stack
                    direction="column"
                    spacing={2}
                    alignItems="flex-start"
                >
                    {/* Sail Area */}
                    <Box>
                        <Typography component="span" fontWeight="bold">
                            Area:{' '}
                        </Typography>
                        <Typography component="span">
                            {quoteData.AdjustedSquareFeet?.toFixed(0) || 'N/A'} sq. ft.
                        </Typography>
                    </Box>

                    {/* LP Adjuster below if shown */}
                    {showLp && (
                        <LPAdjuster
                            quoteId={quoteId}
                            disabled={state.isLoading}
                            initialValue={quoteData.PercentLP}
                            onError={handleLPError}
                        />
                    )}
                </Stack>

                {/* Notes section after the main items, full width */}
                {quoteData.Notes && (
                    <Box sx={{ mt: 1 }}>  {/* Added margin-top for spacing */}
                        <Typography variant="subtitle1" >
                            Notes
                        </Typography>
                        <Typography variant="body2">
                            {quoteData.Notes}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default CoreSailInfo;